import { OFFER, OPENING, SubjectType } from '../types';
import {
  ApprovalPlan,
  ApprovalRequest,
  ApprovalRequestList,
  Approver,
  ApproverStatus,
  Level,
  LevelStatus,
  PreviousSubmission,
  RequestStatus,
  RuleGroup,
} from './types';

/**
 * Turns the generated client's all-optional response shapes into the types the
 * rest of this feature reads (AXHE-3947). See `types.ts` for why.
 *
 * Every field the UI branches on gets a defined fallback, and every field it
 * only displays stays nullable — a missing approver name renders as an unnamed
 * row, which is the honest answer for a soft-deleted user, while a missing
 * status would silently pick a pill colour. `status` and `id` are the two the
 * server cannot omit, so an absent one is a contract break rather than a state:
 * the fallbacks below are chosen so a break reads as "nothing to act on" instead
 * of offering a control.
 */

// The client types these as loose unions of the enum plus undefined, so the
// narrowing is a membership test rather than a cast.
const REQUEST_STATUSES: RequestStatus[] = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
];
const LEVEL_STATUSES: LevelStatus[] = [
  'pending',
  'active',
  'approved',
  'rejected',
  'skipped',
];
const APPROVER_STATUSES: ApproverStatus[] = ['pending', 'approved', 'rejected'];
const SUBJECT_TYPES: SubjectType[] = [OFFER, OPENING];

const oneOf = <T extends string>(
  values: T[],
  value: unknown,
  fallback: T,
): T => (values.includes(value as T) ? (value as T) : fallback);

const text = (value: string | null | undefined): string | null => value ?? null;

/**
 * A jsonb map, coerced to the string map the offer form reads.
 *
 * The column is schemaless, so a number written by an API client comes back as
 * one — and `values[field.key] ?? field.value` would then put a number into a
 * text input, which React re-renders as a string anyway but which `effectiveValues`
 * would submit as a number. Coercing here keeps one type on the way in and on
 * the way back out. A null entry is dropped rather than rendered as "null".
 */
const strings = (raw: unknown): Record<string, string> => {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};

  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>)
      .filter(([, value]) => value != null)
      .map(([key, value]) => [key, String(value)]),
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any -- the generated response
   types are structurally anonymous and deeply optional; naming them here would
   mean restating the whole tree, which is exactly what this module exists to
   stop. Every read below is guarded. */

const toApprover = (raw: any): Approver => ({
  id: text(raw?.user_id),
  name: text(raw?.name),
  email: text(raw?.email),
  // A role NAME, not a primary/backup flag — `backup` carries that.
  role: text(raw?.role),
  backup: raw?.backup === true,
  status: oneOf(APPROVER_STATUSES, raw?.status, 'pending'),
  decidedAt: text(raw?.decided_at),
  comment: text(raw?.comment),
  isViewer: raw?.is_viewer === true,
});

const toLevel = (raw: any): Level => ({
  stepId: raw?.step_id ?? 0,
  position: raw?.position ?? 0,
  name: text(raw?.name),
  // `pending` rather than `active`: an unreadable status must not render as a
  // level that is currently being asked of somebody.
  status: oneOf(LEVEL_STATUSES, raw?.status, 'pending'),
  approverLogic: raw?.approver_logic === 'all' ? 'all' : 'any',
  // Tri-state, and the third value matters: `false` is the only one that means
  // "the rules said this level did not apply". A rejection elsewhere closes
  // sibling levels as `skipped` too, and those carry null or true — see the
  // shared chain's `levelState`.
  conditionMet:
    typeof raw?.condition_met === 'boolean' ? raw.condition_met : null,
  decidedAt: text(raw?.decided_at),
  // Derived from the timestamp, because the server publishes no boolean: a
  // level escalates by having `escalated_at` stamped on it. Reading a bare
  // `escalated` fails closed and silently — `visibleApprovers` would hide every
  // backup for good, on the levels that exist to bring them in.
  escalated: text(raw?.escalated_at) !== null,
  escalateAt: text(raw?.escalate_at),
  needsAttention: raw?.needs_attention === true,
  conditionUnevaluatable: raw?.condition_unevaluatable === true,
  approvers: (raw?.approvers ?? []).map(toApprover),
  carried: raw?.carried === true,
});

const toRuleGroup = (raw: any): RuleGroup => ({
  id: raw?.rule_id ?? null,
  name: text(raw?.name),
  levels: (raw?.levels ?? []).map(toLevel),
});

const PLAN_OUTCOMES = ['approval', 'send', 'hold'];

/**
 * `approval.plan`, or null when the server sent none: a submit from where the
 * offer stands decides nothing. An outcome this build does not know reads as
 * null too, so the panel falls back rather than naming a result it cannot.
 */
export const toApprovalPlan = (raw: any): ApprovalPlan | null => {
  if (!raw || !PLAN_OUTCOMES.includes(raw.outcome)) return null;
  return {
    outcome: raw.outcome,
    rules: (raw.rules ?? []).map(toRuleGroup),
    digest: text(raw.digest),
  };
};

const toPreviousSubmission = (raw: any): PreviousSubmission => ({
  number: raw?.number ?? 0,
  status:
    raw?.status === 'approved' || raw?.status === 'cancelled'
      ? raw.status
      : 'rejected',
  submittedAt: text(raw?.submitted_at),
  decidedAt: text(raw?.decided_at),
  rejectionReason: text(raw?.rejection_reason),
});

export const toApprovalRequest = (raw: any): ApprovalRequest => ({
  id: raw?.external_id ?? '',
  // Null rather than a fallback type, and the only field here that gets one.
  // Every other unreadable value has a state that is safe to assume; this one
  // decides which control a row offers, and guessing "offer" would put a Review
  // button on a row with no document behind it.
  subjectType: SUBJECT_TYPES.includes(raw?.subject_type) ? raw.subject_type : null,
  subjectId: text(raw?.subject_id),
  status: oneOf(REQUEST_STATUSES, raw?.status, 'pending'),
  submittedAt: text(raw?.submitted_at),
  decidedAt: text(raw?.decided_at),
  subject: {
    applicantName: text(raw?.subject?.applicant_name),
    positionName: text(raw?.subject?.position_name),
    documentTitle: text(raw?.subject?.document_title),
    documentUrl: text(raw?.subject?.document_url),
    // Nullable rather than defaulted: every consumer treats an unreadable value
    // as "cannot be edited", which is the safe answer for a field that decides
    // whether a control rewriting a legal document is offered at all.
    offerStatus: text(raw?.subject?.offer_status),
    applicantId: text(raw?.subject?.applicant_id),
    offerLetterTemplateId: text(raw?.subject?.offer_letter_template_id),
    fieldValues: strings(raw?.subject?.field_values),
    conditionValues: strings(raw?.subject?.condition_values),
    editedFieldKeys: (raw?.subject?.edited_keys?.field_values ?? []).filter(
      (key: unknown): key is string => typeof key === 'string',
    ),
  },
  rules: (raw?.rule_groups ?? []).map(toRuleGroup),
  previousSubmissions: (raw?.previous_submissions ?? []).map(
    toPreviousSubmission,
  ),
  // Element-wise, not just whole-array. This is the one field that decides
  // whether the decision controls render at all, and a single `null` in the
  // array reaches `actionableStepIds[0]`, passes DecisionFooter's
  // `stepId === undefined` check, and POSTs `step_id: null`. Filtering to real
  // numbers is what makes a contract break read as "nothing to act on", which is
  // this module's stated job.
  actionableStepIds: (raw?.viewer?.actionable_step_ids ?? []).filter(
    (id: unknown): id is number =>
      typeof id === 'number' && Number.isFinite(id),
  ),
  // Strict equality, so anything the server did not say reads as "no" — the
  // queue rows, where it is not computed, and a payload this build cannot read.
  // The alternative default renders an edit control that 403s when pressed.
  viewerCanManageOffer: raw?.viewer?.can_manage_offer === true,
});

export const toApprovalRequestList = (raw: any): ApprovalRequestList => {
  const rows = (raw?.requests ?? []).map(toApprovalRequest);

  return {
    rows,
    // Both default to 1, which reads as "one page, you are on it" — the state in
    // which a pager renders nothing. A missing `total_pages` must not offer a
    // Next that pages into an empty list.
    currentPage: raw?.pagination?.current_page ?? 1,
    totalPages: raw?.pagination?.total_pages ?? 1,
  };
};

/* eslint-enable @typescript-eslint/no-explicit-any */
